/**
 * Invitations API Client
 *
 * Org-scoped invitation endpoints go through the centralized API SDK for
 * consistent authentication, token refresh, and 401 handling — and throw
 * ApiClientError on failure (same contract as domainsApi).
 *
 * The by-token endpoints are public (pre-auth invitation landing) and use
 * apiCall's skipAuth pipeline, returning the ApiResult envelope instead of
 * throwing — the landing page renders a not-found state from it.
 */

import { api } from './sdk';
import { apiCall } from './client';
import { buildListQuery, type Paginated, type PaginationParams } from './pagination';
import type { ApiResult } from './client';

// Re-export error handling utilities for consumers
export { ApiClientError, isApiClientError } from './sdk';

export interface InviteRow {
  id: string;
  token: string;
  recipient_email: string;
  recipient_name: string | null;
  coupon: string;
  status: 'pending' | 'redeemed' | 'expired' | 'revoked';
  redeemed_by_user_id: string | null;
  redeemed_at: string | null;
  expires_at: string;
  email_sent_at: string | null;
  created_at: string;
  redeemed_by_email: string | null;
}

export interface InviteQuota {
  count_remaining: number;
  coupon: string | null;
  summary: string | null;
}

export interface AvailableCoupon {
  name: string;
  summary: string;
}

export interface AvailableCoupons {
  coupons: AvailableCoupon[];
  default: string;
}

export interface CreateInviteRequest {
  recipient_email: string;
  recipient_name?: string;
  coupon?: string;
}

export interface CreateInviteResponse {
  id: string;
  token: string;
  email_send_failed: boolean;
}

export interface PublicInvite {
  status: 'pending' | 'redeemed' | 'expired' | 'revoked';
  inviter_display_name: string;
  recipient_email: string;
  discount_summary: string;
  expires_at: string;
}

export const invitationsApi = {
  /**
   * Get invitations sent by the current user (paginated).
   * @throws {ApiClientError} On API failure (403 if missing create:invitations)
   */
  listInvites: (params: PaginationParams = {}): Promise<Paginated<InviteRow>> =>
    api.get<Paginated<InviteRow>>(`/api/invitations${buildListQuery(params)}`),

  /**
   * Get the current user's remaining invite quota.
   * @throws {ApiClientError} On API failure (403 if missing create:invitations)
   */
  getQuota: (): Promise<InviteQuota> =>
    api.get<InviteQuota>('/api/invitations/quota'),

  /**
   * Get the coupons the current user may attach to an invite.
   * @throws {ApiClientError} On API failure (403 if missing create:invitations)
   */
  getAvailableCoupons: (): Promise<AvailableCoupons> =>
    api.get<AvailableCoupons>('/api/invitations/available-coupons'),

  /**
   * Create and send an invitation.
   * @throws {ApiClientError} On API failure (409 if already invited)
   */
  createInvite: (request: CreateInviteRequest): Promise<CreateInviteResponse> =>
    api.post<CreateInviteResponse>('/api/invitations', request),

  /**
   * Resend a pending invitation's email.
   * @throws {ApiClientError} On API failure (404 if not found)
   */
  resendInvite: (id: string): Promise<{ email_send_failed: boolean }> =>
    api.post<{ email_send_failed: boolean }>(`/api/invitations/${encodeURIComponent(id)}/resend`),

  /**
   * Revoke a pending invitation.
   * @throws {ApiClientError} On API failure (404 if not found)
   */
  revokeInvite: (id: string): Promise<{ revoked: true }> =>
    api.delete<{ revoked: true }>(`/api/invitations/${encodeURIComponent(id)}`),
};

/** Public (unauthenticated) lookup for the invitation landing page. */
export const getInviteByToken = (
  token: string,
): Promise<ApiResult<PublicInvite>> =>
  apiCall(`/api/invitations/by-token/${encodeURIComponent(token)}`, { method: 'GET', skipAuth: true });

/** Redeem a pending invite for the now-authenticated user. `summary` is the
 * human-readable discount actually applied (varies per coupon). */
export const redeemInviteByToken = (
  token: string,
): Promise<ApiResult<{ redeemed: true; summary: string }>> =>
  apiCall(`/api/invitations/by-token/${encodeURIComponent(token)}/redeem`, { method: 'POST' });
